import type { ReactElement } from 'react';
import type { ExtensionVersion } from '@kirin/api-client/contracts/extensions';
import { Link } from 'react-router-dom';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { RelativeTimeWithTooltip } from '@/components/shared/RelativeTimeWithTooltip';
import { VersionUsersCell } from './CellRenderers';
import { SecuritySummaryCell } from '@/components/shared/extensions/SecuritySummaryCell';
import { RiskEstimateCell } from '@/components/shared/extensions/RiskEstimateCell';
import { ScanStatusCell } from '@/components/shared/extensions/ScanStatusCell';
import {
  isNotApplicable,
  marketplaceLabel,
} from '@/components/shared/extensions/agentmesh-risk-labels';

interface ExtensionVersionsTableProps {
  versions: ExtensionVersion[];
  /** KIR-5281: called with the clicked version row. */
  onUserClick?: (version: string) => void;
  organizationId: string;
  extensionId: string;
  capabilityDetectionEnabled: boolean;
  /**
   * KIR-1928 / KIR-5208: per-version AgentMesh Scan status, Risk and "View in
   * AgentMesh" link, in place of Kirin's own Risk and scan result.
   */
  agentmeshRiskEnabled?: boolean;
  /**
   * The extension's Source flag: each version of a built-in without an
   * AgentMesh verdict reads "Not applicable" (KIR-4830).
   */
  isBuiltIn?: boolean;
}

/**
 * Table showing extension version details in the expanded row.
 * Matches legacy ExtensionsTab implementation exactly.
 * Displays version string, clickable user count, and first seen date.
 */
export function ExtensionVersionsTable({
  versions,
  onUserClick,
  organizationId,
  extensionId,
  capabilityDetectionEnabled,
  agentmeshRiskEnabled = false,
  isBuiltIn = false,
}: ExtensionVersionsTableProps): ReactElement {
  // AgentMesh is the only source of extension risk while its flag is on;
  // Kirin's own checks surface as alerts instead (KIR-5208).
  const showKirinRisk = capabilityDetectionEnabled && !agentmeshRiskEnabled;
  if (versions.length === 0) {
    return (
      <div className="p-4 text-center text-muted-foreground text-sm">
        No version information available
      </div>
    );
  }

  return (
    <div className="pl-8 py-4">
      <div className="text-sm font-medium mb-2">Versions:</div>
      <Table className="text-xs">
        <TableHeader className="border-b bg-muted">
          <TableRow className="!bg-transparent hover:!bg-transparent">
            <TableHead>Version</TableHead>
            <TableHead className="text-center">Users</TableHead>
            <TableHead>First Seen</TableHead>
            {showKirinRisk && <TableHead>Risk</TableHead>}
            {showKirinRisk && <TableHead />}
            {agentmeshRiskEnabled && <TableHead>Scan status</TableHead>}
            {agentmeshRiskEnabled && <TableHead>Risk</TableHead>}
            {agentmeshRiskEnabled && <TableHead />}
            {agentmeshRiskEnabled && <TableHead>Marketplace</TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {versions.map(version => (
            <TableRow
              key={version.version}
              className="border-b hover:bg-muted/50"
            >
              <TableCell>{version.version}</TableCell>
              <TableCell className="text-center">
                <VersionUsersCell
                  userCount={version.userCount}
                  onClick={
                    onUserClick ? () => onUserClick(version.version) : undefined
                  }
                />
              </TableCell>
              <TableCell className="text-muted-foreground">
                <RelativeTimeWithTooltip date={version.firstSeenAt} />
              </TableCell>
              {showKirinRisk && (
                <TableCell>
                  <SecuritySummaryCell summary={version.securitySummary} />
                </TableCell>
              )}
              {showKirinRisk && (
                <TableCell>
                  {version.securitySummary != null ? (
                    <Link
                      to={`/${organizationId}/inventory/extensions/${encodeURIComponent(extensionId)}/scan-result?version=${encodeURIComponent(version.version)}`}
                      className="text-primary hover:underline"
                    >
                      View scan result →
                    </Link>
                  ) : null}
                </TableCell>
              )}
              {agentmeshRiskEnabled && (
                <TableCell>
                  <ScanStatusCell
                    scanStatus={version.scanStatus}
                    // The extension's Source flag (KIR-4830): a version of a
                    // built-in without an AgentMesh verdict is Not applicable.
                    isBuiltIn={isBuiltIn}
                  />
                </TableCell>
              )}
              {agentmeshRiskEnabled && (
                <TableCell>
                  <RiskEstimateCell
                    riskEstimate={version.riskEstimate}
                    notApplicable={isNotApplicable(
                      version.scanStatus,
                      isBuiltIn
                    )}
                  />
                </TableCell>
              )}
              {agentmeshRiskEnabled && (
                <TableCell>
                  {version.agentmeshUrl ? (
                    <a
                      href={version.agentmeshUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="whitespace-nowrap text-primary hover:underline"
                    >
                      View in AgentMesh ↗
                    </a>
                  ) : null}
                </TableCell>
              )}
              {agentmeshRiskEnabled && (
                // KIR-5361: where AgentMesh found this version's result.
                <TableCell className="whitespace-nowrap text-muted-foreground">
                  {marketplaceLabel(version.riskEstimate?.marketplace) ?? '—'}
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

ExtensionVersionsTable.displayName = 'ExtensionVersionsTable';
